import React, { useState, useMemo, useEffect } from 'react';
import { Box, Typography, List, ListItem, ListItemText, Switch, FormControl, InputLabel, Select, MenuItem, Divider, useMediaQuery } from '@mui/material';
import { Link } from 'react-router-dom';
import { useTheme } from '@mui/material';

function IngredientsList({ inventory, togglePossession, recipes, ingredients }) {
  // Initialize state from localStorage or default
  const [sortOrder, setSortOrder] = useState(localStorage.getItem('ingredientSortOrder') || 'type');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Persist sortOrder to localStorage
  useEffect(() => {
    localStorage.setItem('ingredientSortOrder', sortOrder);
  }, [sortOrder]);

  // Memoize typeOrder since it's a static object
  const typeOrder = useMemo(() => ({
    'Juices': 1,
    'Syrups': 2,
    'Rums': 3,
    'Spirits': 4,
    'Bitters': 5,
    'Unknown': 6
  }), []); // Empty dependency array since it never changes

  const groupedInventory = useMemo(() => {
    return inventory.reduce((acc, ing) => {
      const type = ing.type || 'Unknown';
      acc[type] = acc[type] || [];
      acc[type].push(ing);
      return acc;
    }, {});
  }, [inventory]);

  const sortedTypes = useMemo(() => {
    return Object.keys(groupedInventory).sort((a, b) => {
      return (typeOrder[a] || 4) - (typeOrder[b] || 4);
    });
  }, [groupedInventory, typeOrder]);

  const sortedInventory = useMemo(() => {
    if (sortOrder === 'alpha') {
      return [...inventory].sort((a, b) => a.name.localeCompare(b.name));
    } else {
      const result = [];
      sortedTypes.forEach(type => {
        result.push(...groupedInventory[type]);
      });
      return result;
    }
  }, [inventory, sortOrder, sortedTypes, groupedInventory]);

  // Group sorted inventory by first letter when alphabetical
  const groupedByLetter = useMemo(() => {
    if (sortOrder === 'alpha') {
      return sortedInventory.reduce((acc, ing) => {
        const firstLetter = ing.name[0].toUpperCase();
        acc[firstLetter] = acc[firstLetter] || [];
        acc[firstLetter].push(ing);
        return acc;
      }, {});
    }
    return null;
  }, [sortedInventory, sortOrder]);

  const recipeCounts = useMemo(() => {
    const counts = new Map();
    inventory.forEach(ing => {
      const count = recipes.filter(recipe => 
        recipe.ingredients.some(i => i.ingredientId === ing.id)
      ).length;
      counts.set(ing.id, count);
    });
    return counts;
  }, [recipes, inventory]);

  const handleSortChange = (event) => {
    setSortOrder(event.target.value);
  };

  const renderIngredientItem = (ing) => {
    const recipesUsingIngredient = recipeCounts.get(ing.id);
    return (
      <ListItem 
        key={ing.id} 
        sx={{ 
          alignItems: 'flex-start', 
          py: { xs: 0.25, sm: 0.25 },
          backgroundColor: 'transparent',
          transitionProperty: 'background-color',
          transitionDuration: '150ms',
          transitionTimingFunction: 'ease-out',
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
            borderRadius: { xs: 2, sm: 1 },
            transitionDuration: '0ms',
          },
          '&:active': {
            backgroundColor: theme.palette.action.selected,
          },
          minHeight: { xs: '48px', sm: 'auto' },
        }}
      >
        <ListItemText 
          primary={
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.1,
              '& a:hover': {
                textDecoration: 'underline',
                color: theme.palette.primary.main
              }
            }}>
              <Switch
                checked={ing.possessed}
                onChange={() => togglePossession(ing.id)}
                color="primary"
                size={isMobile ? "medium" : "small"}
              />
              <Link 
                to={`/ingredient/${encodeURIComponent(ing.id)}`} 
                style={{ 
                  textDecoration: 'none', 
                  color: 'inherit',
                }}
              >
                <Typography sx={{ 
                  fontSize: { xs: '0.85rem', sm: '0.85rem' },
                  fontWeight: isMobile ? 500 : 400,
                  lineHeight: 1.0,
                }}>
                  {ing.name}
                </Typography>
              </Link>
            </Box>
          } 
          secondary={
            <Typography 
              variant="caption" 
              component="span"
              sx={{ 
                mt: { xs: 0, sm: 0 },
                fontSize: { xs: '0.65rem', sm: '0.65rem' },
                lineHeight: 1.0,
                color: theme.palette.text.secondary,
              }}
            >
              Used in {recipesUsingIngredient} {recipesUsingIngredient === 1 ? 'recipe' : 'recipes'}
              <Box 
                component={Link} 
                to={`/ingredient/${encodeURIComponent(ing.id)}`}
                sx={{
                  marginLeft: 0.25,
                  textDecoration: 'none',
                  color: theme.palette.primary.main,
                  '&:hover': {
                    textDecoration: 'underline'
                  },
                  '&:visited': {
                    color: theme.palette.primary.main
                  }
                }}
              >
                (View Recipes)
              </Box>
            </Typography>
          }
        />
      </ListItem>
    );
  };

  return (
    <Box sx={{ 
      backgroundColor: theme.palette.background.default,
      color: theme.palette.text.primary,
      p: { xs: 1, sm: 0 },
    }}>
      <Box sx={{ mb: 0.5 }}>
        <FormControl 
          fullWidth 
          sx={{ mt: 0.5 }}
          size={isMobile ? "small" : "small"}
        >
          <InputLabel>Sort Ingredients By</InputLabel>
          <Select
            value={sortOrder}
            onChange={handleSortChange}
            label="Sort Ingredients By"
            sx={{
              backgroundColor: theme.palette.background.paper,
              borderRadius: { xs: 2, sm: 1 },
            }}
          >
            <MenuItem value="type">By Type</MenuItem>
            <MenuItem value="alpha">Alphabetically</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <List 
        dense={!isMobile}
        sx={{ 
          backgroundColor: theme.palette.background.paper,
          borderRadius: { xs: 2, sm: 1 },
          overflow: 'hidden',
          mb: { xs: 4, sm: 0 },
        }}
      >
        {sortOrder === 'type' ? (
          sortedTypes.map(type => (
            <React.Fragment key={type}>
              <ListItem 
                sx={{ 
                  paddingLeft: 2, 
                  paddingTop: { xs: 0.75, sm: 0.25 },
                  paddingBottom: { xs: 0.5, sm: 0.25 },
                  backgroundColor: theme.palette.action.hover,
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                }}
              >
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 'bold', 
                    color: 'text.secondary',
                    fontSize: { xs: '0.9rem', sm: '0.875rem' },
                  }}
                >
                  {type} ({groupedInventory[type].length})
                </Typography>
              </ListItem>
              {groupedInventory[type].map(ing => renderIngredientItem(ing))}
              <Divider sx={{ my: 0.25 }} />
            </React.Fragment>
          ))
        ) : (
          Object.keys(groupedByLetter || {}).sort().map(letter => (
            <React.Fragment key={letter}>
              <ListItem 
                sx={{ 
                  paddingLeft: 2, 
                  paddingTop: { xs: 0.75, sm: 0.5 },
                  paddingBottom: { xs: 0.5, sm: 0.5 },
                  backgroundColor: theme.palette.action.hover,
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                }}
              >
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 'bold', 
                    color: 'text.secondary',
                    fontSize: { xs: '0.9rem', sm: '0.875rem' },
                  }}
                >
                  {letter}
                </Typography>
              </ListItem>
              {groupedByLetter[letter].map(ing => renderIngredientItem(ing))}
              <Divider sx={{ my: 0.5 }} />
            </React.Fragment>
          ))
        )}
      </List>
    </Box>
  );
}

export default IngredientsList;